import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  InnerPageToggleButton,
  LanguagesTabs,
  Spin,
} from '@wajeez-web/components/ui';
import { useAuthSelector } from '@wajeez-web/stores/auth';
import { useForm, DeepPartial, Path, useFieldArray } from 'react-hook-form';
import { Languages, languages } from '@wajeez-web/core/utils';

import { FormField, FormFieldV2 } from './Types';
import WajeezFormInput from './WajeezFormInput';
import WajeezFormInputV2 from './WajeezFormInputV2';

export interface ProfileInput {
  languageId?: number;
  language?: string;
  [key: string]: any;
}

export interface MultilingualInput {
  profiles?: Array<ProfileInput>;
}

export type ArrayElement<ArrayType extends readonly unknown[] | undefined> =
  ArrayType extends readonly (infer ElementType)[] ? ElementType : never;

export interface WajeezMultilingualFormBuilderProps<
  TFormFields extends MultilingualInput
> {
  isLoading?: boolean;
  title?: string;
  desc?: string;
  formValues?: DeepPartial<TFormFields>;
  onSubmit: (props: TFormFields) => void;
  rows: {
    [key in keyof Omit<TFormFields, 'profiles'>]: FormField;
  };
  profileRows: {
    [key in keyof Omit<
      ArrayElement<TFormFields['profiles']>,
      'languageId'
    >]: FormField;
  };
  hasSubmitButton?: boolean;
  hasToggleButton?: boolean;
  isUpdate?: boolean;
  onCancel?: () => void;
}

const WajeezMultilingualFormBuilder = <TFormFields extends MultilingualInput>({
  title,
  desc,
  hasSubmitButton,
  formValues,
  onSubmit,
  rows,
  profileRows,
  hasToggleButton,
  isUpdate,
  onCancel,
}: WajeezMultilingualFormBuilderProps<TFormFields>) => {
  const navigate = useNavigate();

  const [selectedProfile, setSelectedProfile] = useState<Languages>(
    Languages.AR
  );
  const [isLanguageTabChanging, setIsLanguageTabChanging] = useState(false);
  const [addedLanguages, setAddedLanguages] = useState<Languages[]>();

  const permissions = useAuthSelector((s) => s.auth.permission ?? []);

  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    setError,
    trigger,
    watch,
    formState: { errors },
  } = useForm<TFormFields>({
    defaultValues: formValues,
    mode: 'onSubmit',
  });

  // watch((data) => {
  //   console.log(data);
  // });

  const { fields, append, remove, update } = useFieldArray<
    MultilingualInput,
    'profiles',
    'languageId'
  >({
    control: control as any,
    name: 'profiles',
  });

  const selectedLanguage = languages.find((l) => l.value === selectedProfile);

  const currentProfileIndex = fields?.findIndex(
    (s) => s.languageId === selectedProfile
  );

  useEffect(() => {
    setAddedLanguages(fields?.map((f) => f.languageId as number));
  }, [fields?.length]);

  const getFormRows = () => {
    return (
      <div className="space-y-8">
        {title && (
          <div className="">
            <h3 className="ms-2 lg:ms-0 text-2xl flex align-middle font-bold space-s-2 tracking-tight leading-none  ">
              {hasToggleButton && <InnerPageToggleButton />}{' '}
              <div className="mt-1">{title}</div>
            </h3>
            <p className=" text-sm text-gray-600">{desc}</p>
          </div>
        )}
        <div>
          <div className="space-y-6">
            <div className={`grid grid-cols-${3} gap-3 `}>
              {Object.entries(rows).map(({ '0': key, '1': value }, i) => {
                const name = key as Path<TFormFields>;
                const item = value as FormFieldV2;
                return (
                  <div
                    key={key}
                    tabIndex={i}
                    className={`md:col-span-${3} col-span-${3} `}
                  >
                    <WajeezFormInputV2
                      control={control}
                      name={name}
                      fieldMeta={item}
                      isReadOnly={
                        item.permission &&
                          !permissions.includes(item.permission)
                          ? true
                          : false
                      }
                      id={'name'}
                    />
                    {/* {getInput(name, item)} */}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getProfileRows = (field: ProfileInput | undefined, index: number) => {
    return (
      <div
        className={`space-y-8 px-4 pt-4 pb-8`}
        style={{ backgroundColor: selectedLanguage?.activeColor }}
        key={`${field?.languageId}-${field?.language}`}
      >
        <div>
          <div className="space-y-6">
            <div className={`grid grid-cols-${3} gap-3 `}>
              {Object.entries(profileRows).map(
                ({ '0': key, '1': value }, i) => {
                  const name = (`profiles.${index}.` +
                    key) as Path<TFormFields>;
                  const item = value as FormFieldV2;
                  return (
                    <div
                      key={key}
                      tabIndex={i}
                      className={`md:col-span-${3} col-span-${3} `}
                    >
                      <WajeezFormInputV2
                        control={control}
                        name={name}
                        fieldMeta={item}
                        isReadOnly={
                          item.permission &&
                            !permissions.includes(item.permission)
                            ? true
                            : false
                        }
                        id={'name'}
                      />
                      {/* {getInput(name, item)} */}
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative w-full">
      <div className="absolute top-[105%] left-0 flex justify-end space-s-2 rounded-md">
        <Button
          // refName={"create-book"}
          title="Cancel"
          onClick={() => {
            Swal.fire({
              title: t(`general.${'Are you sure?'}`, '') as string,
              text: t(
                `general.${"You won't be able to revert this!"}`,
                ''
              ) as string,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: t(`general.${'Yes'}`, '') as string,
              cancelButtonText: t(`general.${'Cancel'}`, '') as string,
            }).then((result) => {
              if (result.isConfirmed) {
                if (onCancel) {
                  onCancel();
                } else {
                  navigate(-1);
                }
              }
            });
          }}
          color="textPrimary"
          type="ghost"
        />
        <Button
          // refName={"create-book"}
          title="save"
          onClick={() => {
            console.log('Submitting form with values:', watch());
            handleSubmit(onSubmit, (error) => {
              console.log(error);
            })();
          }}
          bg="primary"
          color="white"
          loading={isUpdate}
          disabled={isUpdate}
        />
      </div>
      {getFormRows()}
      <div className="mt-4">
        <LanguagesTabs
          languages={languages}
          selectedLanguageValue={selectedProfile}
          onSelect={(value) => {
            setSelectedProfile(value);

            trigger('profiles' as Path<TFormFields>);

            if (value !== selectedProfile) {
              setIsLanguageTabChanging(true);

              setTimeout(() => {
                setIsLanguageTabChanging(false);
              }, 300);
            }
          }}
          onAddedLanguagesChange={(languages, language) => {
            const formProfileIndex = fields?.findIndex(
              (f) => f.languageId === language
            );

            const fieldEmptyObj: { [key: string]: any } = {};

            for (const key in fields[0]) {
              fieldEmptyObj[key] = null;
            }

            if (addedLanguages && languages.length > addedLanguages.length) {
              append({
                ...fieldEmptyObj,
                languageId: language,
              });
            } else if (
              addedLanguages &&
              languages.length < addedLanguages.length &&
              formProfileIndex !== -1
            ) {
              update(formProfileIndex, fieldEmptyObj);

              remove(formProfileIndex);
            }

            setAddedLanguages(languages);
          }}
          addedLanguages={fields?.map((f) => {
            return f.languageId as number;
          })}
        />
      </div>
      {isLanguageTabChanging ? (
        <div
          className="w-full h-[400px] flex justify-center items-center"
          style={{
            backgroundColor: selectedLanguage?.activeColor,
            color: selectedLanguage?.inactiveColor,
          }}
        >
          <Spin colorStyle="text-currentcolor" />
        </div>
      ) : (
        currentProfileIndex !== -1 &&
        currentProfileIndex !== undefined &&
        getProfileRows(fields?.[currentProfileIndex], currentProfileIndex)
      )}
    </div>
  );
};

export default WajeezMultilingualFormBuilder;
